import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { logEvent } from "@/lib/analytics";
import { useTenantConfig } from "@/context/TenantConfigContext";
import { DateField } from "@/components/DateField";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CaretDown, CaretUp, Plus } from "@phosphor-icons/react";

// The 4 fields that create a USEFUL first receivable (name + id + outstanding + due date → DPD derives).
const QUICK_FIELDS = [
  ["borrower_name", "Customer Name"],
  ["loan_id", "Account / Loan / Invoice ID"],
  ["outstanding_amount", "Outstanding Amount"],
  ["emi_due_date", "Payment Due Date"],
];

// Everything else the existing full Create Account form exposes — revealed under "Add more details".
// Uses the SAME field keys and the SAME POST /accounts endpoint (no separate model, no duplicates).
const MORE_FIELDS = [
  ["customer_id", "Customer ID"], ["mobile", "Mobile"], ["alternate_mobile", "Alt. Mobile"], ["email", "Email"],
  ["city", "City"], ["state", "State"], ["pincode", "Pincode"],
  ["loan_amount", "Loan Amount"], ["receivable_amount", "Receivable Amount"], ["disbursal_date", "Disbursal Date"],
  ["product", "Loan Product"], ["loan_tenure", "Tenure"], ["emi_amount", "EMI Amount"],
  ["principal_outstanding", "Principal Os"], ["interest_outstanding", "Interest Os"],
  ["total_overdue_amount", "Total Overdue"], ["region", "Region"],
];

const NUMERIC = new Set(["loan_amount", "receivable_amount", "outstanding_amount", "principal_outstanding", "interest_outstanding", "total_overdue_amount", "emi_amount"]);

const emptyForm = () => ({
  customer_id: "", loan_id: "", borrower_name: "", mobile: "", alternate_mobile: "", email: "",
  city: "", state: "", pincode: "", loan_amount: "", receivable_amount: "", disbursal_date: "",
  product: "", loan_tenure: "", emi_amount: "", outstanding_amount: "", principal_outstanding: "",
  interest_outstanding: "", emi_due_date: "", total_overdue_amount: "", region: "", status: "active",
});

// Core fields carried over when cloning an existing account. Loan ID is deliberately left blank
// (must be unique per tenant), and no history / child records are copied.
const CLONE_KEYS = [
  "customer_id", "borrower_name", "mobile", "alternate_mobile", "email", "city", "state", "pincode",
  "loan_amount", "receivable_amount", "disbursal_date", "product", "loan_tenure", "emi_amount",
  "outstanding_amount", "principal_outstanding", "interest_outstanding", "emi_due_date",
  "total_overdue_amount", "region", "status",
];
export const buildClonePrefill = (acc) => {
  const out = { ...emptyForm(), loan_id: "" };
  CLONE_KEYS.forEach((k) => { if (acc?.[k] !== undefined && acc?.[k] !== null) out[k] = acc[k]; });
  return out;
};

// Reusable Quick Add — shows the 4 minimum fields, expands to the full existing field set on demand.
// On success it fires `manual_account_created` and opens the newly created account detail.
// When `prefill` is provided it acts as "Clone account": the form is pre-populated (Loan ID blank).
export function QuickAddAccountDialog({ open, onOpenChange, source = "accounts_page", prefill = null }) {
  const navigate = useNavigate();
  const { config, terms } = useTenantConfig();
  const L = (k, fallback) => config?.label_overrides?.[k] || fallback;
  const [form, setForm] = useState(emptyForm());
  const [more, setMore] = useState(false);
  const [busy, setBusy] = useState(false);
  const isClone = !!prefill;

  useEffect(() => { if (open) { setForm(prefill ? { ...emptyForm(), ...prefill } : emptyForm()); setMore(!!prefill); } }, [open, prefill]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const renderField = ([k, label]) => (
    <div key={k}>
      <Label className="text-xs">
        {L(k, label)}{["loan_id", "borrower_name", "outstanding_amount", "emi_due_date"].includes(k) ? " *" : ""}
      </Label>
      {k === "emi_due_date" ? (
        <DateField value={form[k]} onChange={(v) => setForm((f) => ({ ...f, [k]: v }))} testId={`quick-acc-${k}`} placeholder="Payment due date" />
      ) : (
        <Input value={form[k]} onChange={set(k)} type={NUMERIC.has(k) ? "number" : "text"} className="rounded-sm mt-1" data-testid={`quick-acc-${k}`} />
      )}
    </div>
  );

  const submit = async (followUp = false) => {
    if (!form.loan_id || !form.borrower_name) {
      return toast.error(`${L("borrower_name", "Customer Name")} and ${L("loan_id", "Account ID")} are required`);
    }
    setBusy(true);
    try {
      const { data } = await api.post("/accounts", {
        ...form,
        loan_amount: +form.loan_amount || 0,
        receivable_amount: +form.receivable_amount || 0,
        outstanding_amount: +form.outstanding_amount || 0,
        principal_outstanding: +form.principal_outstanding || 0,
        interest_outstanding: +form.interest_outstanding || 0,
        total_overdue_amount: +form.total_overdue_amount || 0,
        emi_amount: +form.emi_amount || 0,
      });
      logEvent("manual_account_created", { source });
      window.dispatchEvent(new Event("recoveros:usage-changed"));
      window.dispatchEvent(new Event("recoveros:setup-changed"));
      onOpenChange(false);
      if (followUp && data?.account_id) {
        logEvent("start_followup_after_add", { source });
        toast.success("Account created — let’s start a follow-up in RecoverOS Connect");
        navigate(`/connect?account=${data.account_id}&compose=1`);
      } else {
        toast.success("Account created — here's how it looks in RecoverOS");
        if (data?.account_id) navigate(`/accounts/${data.account_id}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create the account");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-lg max-h-[88vh] overflow-y-auto" data-testid="quick-add-account-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Plus size={18} weight="bold" className="text-[#16A34A]" /> {isClone ? `Clone ${terms?.account_noun || "account"}` : `Add one ${terms?.account_noun || "account"}`}</DialogTitle>
          <DialogDescription>{isClone ? "Duplicated from an existing record. Enter a new Account / Loan ID and review the details before saving — history isn't copied." : "Enter one real receivable to see it working inside RecoverOS. DPD is auto-calculated from the Payment Due Date."}</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {QUICK_FIELDS.map(renderField)}
        </div>

        <button
          type="button"
          onClick={() => setMore((v) => !v)}
          className="mt-1 inline-flex items-center gap-1.5 text-sm font-semibold text-[#0B3D91] hover:underline w-fit"
          data-testid="quick-add-more-toggle"
        >
          {more ? <CaretUp size={14} weight="bold" /> : <CaretDown size={14} weight="bold" />}
          {more ? "Hide extra details" : "Add more details"}
        </button>

        {more && (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 border-t border-border pt-3" data-testid="quick-add-more-fields">
            {MORE_FIELDS.map(renderField)}
            <div>
              <Label className="text-xs">{L("status", "Status")}</Label>
              <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
                <SelectTrigger className="rounded-sm mt-1" data-testid="quick-acc-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["active", "ptp", "settled", "closed", "paid"].map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        <DialogFooter className="mt-2 flex-col sm:flex-row gap-2">
          <Button variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)} data-testid="quick-add-cancel">Cancel</Button>
          <Button onClick={() => submit(false)} disabled={busy} variant="outline" className="rounded-sm" data-testid="quick-add-submit">
            {busy ? "Creating…" : "Create & view account"}
          </Button>
          <Button onClick={() => submit(true)} disabled={busy} className="rounded-sm bg-[#16A34A] hover:bg-[#138a3e]" data-testid="quick-add-submit-followup">
            {busy ? "Creating…" : "Create & start follow-up"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
